/**
 * Abstract base class for timeline views using the @o2d/timeline component.
 *
 * Provides infrastructure for:
 * - Toolbar rendering (configuration-driven via TimelineToolbarItem[])
 * - Standard cell-click handler (opens IssueDialog)
 * - Standard cell-hover handler (shows issue popover)
 * - AppState.dateCursor <-> startCursors bridge (reactive synchronization)
 * - RxJS lifecycle management (reconnect-safe via RxjsLifecycleController)
 * - CSS variables propagation (Light DOM: setStyleProperty)
 * - Loading state for async createStartCursors()
 *
 * Concrete views MUST implement:
 * - Axis, Area, DataProvider configurations (factory methods)
 * - Layout template
 * - Initial start cursors (async, may load entities)
 * - Content provider key (for cell-interaction filtering)
 * - Section size CSS variables
 *
 * Concrete views MAY override:
 * - Toolbar items (default: resetToToday + burgerMenu)
 * - Cell-click handler (default: opens IssueDialog)
 * - Cell-hover handler (default: shows issue popover)
 * - Date cursor axis ID (default: 'B', null to disable bridge)
 *
 * Architecture:
 * - Extends AbstractO2dElement from @o2d/shared (Light DOM, utilities)
 * - Composition over Inheritance (Reactive Controllers for cross-cutting concerns)
 * - Max ONE level of application-specific inheritance
 *
 * Related PRD: PRD-021 (AbstractO2dTimelineView - Base Element for o2d-timeline Views)
 * Related ADRs: ADR-007 (AbstractO2dElement mandate), ADR-009 (Light DOM, Composition)
 */

import { StateController } from '@lit-app/state/src/state-controller';
import { AbstractO2dElement } from '@o2d/shared';
import '../components/_forms/ha-form-issue-filters.component';
import type { LayoutTemplate, AreaConfiguration, CellClickedEventDetail, CellHoverEventDetail } from '@o2d/grid-layout';
import type { AxisConfiguration, CrosspointDataProviderConfiguration, ViewportItem } from '@o2d/datasource';
import type { CursorChangedDetail, DataProviderReadyDetail, O2dTimelineElement } from '@o2d/timeline';
import { state, query } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import type { TemplateResult } from 'lit';
import { translate } from 'lit-translate';
import { DateTime, Interval } from 'luxon';
import { container } from 'tsyringe';
import { filter, fromEvent, map, merge, takeUntil, timer } from 'rxjs';
import { ToolbarHelper } from '../helpers/toolbar.helper';
import { defaultTimelineToolbarItems } from '../toolbar-items/o2d-default-toolbar-items';
import type { TimelineToolbarItem } from '../types/timeline-toolbar-item.interface';
import { IssueHelper } from '../domain/helpers/issue.helper';
import { UiHelper } from '../helpers/ui.helper';
import { IssueRepository } from '../domain/repositories/issue.repository';
import { EmployeeRepository } from '../domain/repositories/employee.repository';
import { EmployeeToIssueAssignmentRepository } from '../domain/repositories/employee-to-issue-assignment.repository';
import type { Issue } from '../domain/entities/issue.entity';
import { RxjsLifecycleController } from '../components/_reactive-controllers/rxjs-lifecycle.controller';
import { appState } from '../app.state';
import { axisEntityMetadata } from '../services';
import { TimelineInvalidationService } from '../services/timeline-invalidation.service';
import { FOCUS_TODAY_EVENT } from '../actions/action.types';
import { MessageBroker } from '../message-broker/message-broker';
import type { BaseMessage } from '../message-broker/base-message.interface';
import type { EntityCreatedPayload } from '../message-broker/payloads/entity-created.payload';
import type { EntityDeletedPayload } from '../message-broker/payloads/entity-deleted.payload';
import type { EntityChangedPayload } from '../message-broker/payloads/entity-changed.payload';
import type { Entity } from '../domain/entities/entity';

/**
 * Re-exported so the callers this view already has keep working; the flag itself moved to
 * `helpers/page-leaving.ts` when `user-display-name.controller.ts` became its second user.
 */
export { isPageLeaving } from '../helpers/page-leaving';
import { isPageLeaving } from '../helpers/page-leaving';

/**
 * Calculates the number of milliseconds from the given time until the next midnight.
 * Returns at least 1000ms as a safety margin for edge cases where `now` is very close to midnight.
 *
 * @param now - The current DateTime to calculate from
 * @returns Milliseconds until next midnight, minimum 1000ms
 */
export function calculateMsUntilMidnight(now: DateTime): number {
    const nextMidnight = now.startOf('day').plus({ days: 1 });
    const diff = nextMidnight.diff(now).milliseconds;
    return Math.max(diff, 1000);
}

export abstract class AbstractO2dTimelineView extends AbstractO2dElement {
    // Re-render this view on any appState change so the per-mode filter
    // drawer (sidebar__filter) reacts to todoFilterOpen toggles from the
    // toolbar.
    private readonly _appStateController = new StateController(this, appState);

    /**
     * AppState slot the embedded filter form reads from / writes to. Threaded into
     * the `<ha-form-issue-filters>` mount so a subclass can isolate its filters from
     * the ToDo `filters` slot. Defaults to `'filters'` (ToDo), keeping every existing
     * timeline view byte-for-byte on the ToDo slot; `ResourcePlanningView` overrides
     * it to `'resourceFilters'`.
     * @protected
     */
    protected filterSlot: 'filters' | 'resourceFilters' = 'filters';

    /**
     * Internal state: Start cursors for all axes.
     * Updated asynchronously during connectedCallback() and reactively via AppState bridge.
     * @internal
     */
    @state() private _startCursors: { axisId: string; value: string | number }[] = [];

    /**
     * Internal state: Axis configurations for the timeline.
     * Set synchronously during connectedCallback() via createAxisConfigurations().
     * @internal
     */
    @state() protected _axisConfigurations: AxisConfiguration<string>[] = [];

    /**
     * Internal state: Area configurations (cellRenderers) for the timeline.
     * Set synchronously during connectedCallback() via createAreaConfigurations().
     * @internal
     */
    @state() protected _areaConfigurations: AreaConfiguration[] = [];

    /**
     * Internal state: Data provider configurations for the timeline.
     * Set synchronously during connectedCallback() via createDataProviderConfigurations().
     * @internal
     */
    @state() private _dataProviderConfigurations: CrosspointDataProviderConfiguration<any, any, any>[] = [];

    /**
     * Internal state: Layout template defining the grid structure.
     * Set synchronously during connectedCallback() via createLayoutTemplate().
     * @internal
     */
    @state() private _layoutTemplate!: LayoutTemplate;

    /**
     * Reference to the embedded o2d-timeline element (Light DOM, so @query resolves
     * against the host). Shared by subclasses for programmatic timeline control
     * (data refresh, axis swap, cursor scroll).
     * @protected
     */
    @query('o2d-timeline') protected _timeline?: O2dTimelineElement;

    /**
     * Loading state: true while createStartCursors() is in progress.
     * Subclasses MAY set this to true during additional async operations (e.g., loading employees).
     * @protected
     */
    @state() protected _isLoading = true;

    /**
     * Guard flag to prevent rapid-click double execution before IssueHelper._openedDialogHash takes effect.
     * @private
     */
    private _isProcessingClick = false;

    /**
     * RxJS lifecycle controller for automatic stream cleanup on disconnect.
     * Provides reconnect-safe disconnected$ Subject.
     */
    protected readonly rxjs = new RxjsLifecycleController(this);

    // =======================================
    // Abstract Members (MUST be implemented)
    // =======================================

    /**
     * Unique view name for ViewState persistence (e.g., 'boards', 'dailys').
     * Used to store and retrieve Y-axis scroll position across view navigations.
     *
     * @readonly
     * @abstract
     */
    protected abstract readonly viewStateName: string;

    /**
     * Factory method for axis configurations specific to this view.
     * Called once during connectedCallback() (synchronous initialization phase).
     *
     * @returns {AxisConfiguration<string>[]} Array of axis configurations (e.g., date axis, employee axis)
     * @abstract
     */
    protected abstract createAxisConfigurations(): AxisConfiguration<string>[];

    /**
     * Factory method for area configurations (cellRenderers).
     * Called once during connectedCallback() (synchronous initialization phase).
     *
     * @returns {AreaConfiguration[]} Array of area configurations defining cell rendering behavior
     * @abstract
     */
    protected abstract createAreaConfigurations(): AreaConfiguration[];

    /**
     * Factory method for data provider configurations.
     * Called once during connectedCallback() (synchronous initialization phase).
     *
     * @returns {CrosspointDataProviderConfiguration<any, any, any>[]} Array of data provider configurations
     * @abstract
     */
    protected abstract createDataProviderConfigurations(): CrosspointDataProviderConfiguration<any, any, any>[];

    /**
     * Layout template defining the grid structure (rows, columns, sections).
     * Called once during connectedCallback() (synchronous initialization phase).
     *
     * @returns {LayoutTemplate} The layout template for this view's timeline
     * @abstract
     */
    protected abstract createLayoutTemplate(): LayoutTemplate;

    /**
     * Initial start cursors for all axes.
     * Called asynchronously during connectedCallback() (AFTER synchronous configurations).
     * MAY load entities (e.g., employees) asynchronously.
     *
     * On error: Logs to console, sets _isLoading = false, shows empty timeline.
     *
     * @returns {Promise<{ axisId: string; value: string | number }[]>} Array of start cursors for each axis
     * @abstract
     */
    protected abstract createStartCursors(): Promise<{ axisId: string; value: string | number }[]>;

    /**
     * Content data provider key for filtering cell interactions.
     * Used to distinguish content cells (that open dialogs) from header cells (ignored).
     *
     * Example: For a timeline with project axis (B) and employee axis (D), key might be "B:D"
     *
     * @readonly
     * @abstract
     */
    protected abstract readonly contentProviderKey: string;

    /**
     * CSS variables for section sizes (Light DOM: applied via setStyleProperty).
     * Called once during connectedCallback() (synchronous initialization phase).
     *
     * Example:
     * ```typescript
     * return {
     *     '--column-section-0-size': '200px',
     *     '--row-section-0-size': '30px',
     * };
     * ```
     *
     * @returns {Record<string, string>} Map of CSS variable names to values
     * @abstract
     */
    protected abstract getSectionSizeVariables(): Record<string, string>;

    // =======================================
    // Y-Axis Entity Type Hook (PRD-043)
    // =======================================

    /**
     * Returns the entity type name for Y-axis live-refresh listeners.
     *
     * Concrete views override this hook to opt into automatic Y-axis refresh on
     * `entity.created` / `entity.deleted` / `entity.changed` events for that type.
     *
     * Default: `null` — no listener is registered (views without a single Y-axis
     * entity type stay inert).
     *
     * @returns Entity type name (e.g. `'IssueStatus'`) or `null` to disable.
     * @protected
     */
    protected getYAxisEntityType(): string | null {
        return null;
    }

    /**
     * Registers MessageBroker listeners for Y-axis entity live-refresh (PRD-043).
     *
     * Registers three listeners that trigger axis-rebuild when a matching
     * `entity.created` / `entity.deleted` / `entity.changed` event fires for the
     * Y-axis entity type returned by `getYAxisEntityType()`.
     *
     * Listener filters match on constructor name (payload structure differs per event):
     * - `entity.created` / `entity.changed`: `msg.payload.prototype.name`
     * - `entity.deleted`: `msg.payload.entity.constructor.name`
     *
     * All three listeners wire `disconnect: this.rxjs.disconnected$` for automatic
     * cleanup on view disconnect — no manual unsubscribe needed.
     *
     * Early-return when `getYAxisEntityType()` returns `null` (view opted out).
     *
     * @private
     */
    private _registerYAxisEntityListeners(): void {
        const entityType = this.getYAxisEntityType();
        if (entityType === null) {
            return;
        }

        const broker = container.resolve(MessageBroker);
        const disconnect = this.rxjs.disconnected$;
        // Filter by prototype.name — payload shape for entity.created / entity.changed
        const matchesPrototype = (
            msg: BaseMessage<EntityCreatedPayload<Entity> | EntityChangedPayload<Entity>>,
        ): boolean => msg.payload.prototype.name === entityType;
        // Filter by entity.constructor.name — entity.deleted carries the instance, not the prototype
        const matchesEntityConstructor = (
            msg: BaseMessage<EntityDeletedPayload<Entity>>,
        ): boolean => msg.payload.entity.constructor.name === entityType;

        broker.registerListener(this, {
            messageType: 'entity.created',
            filterFn: matchesPrototype,
            method: (messages: Array<BaseMessage<EntityCreatedPayload<Entity>>>) =>
                this._onYAxisEntityCreatedOrDeleted(messages),
            disconnect,
        });
        broker.registerListener(this, {
            messageType: 'entity.deleted',
            filterFn: matchesEntityConstructor,
            method: (messages: Array<BaseMessage<EntityDeletedPayload<Entity>>>) =>
                this._onYAxisEntityCreatedOrDeleted(messages),
            disconnect,
        });
        broker.registerListener(this, {
            messageType: 'entity.changed',
            filterFn: matchesPrototype,
            method: (messages: Array<BaseMessage<EntityChangedPayload<Entity>>>) =>
                this._onYAxisEntityChanged(messages),
            disconnect,
        });
    }

    /**
     * Registers MessageBroker listeners for Issue create/delete live-refresh (PRD-062).
     *
     * Triggers a content-provider cache wipe + re-render when an Issue is created
     * or deleted anywhere in the app (cell-click dialog, toolbar "Neue Aufgabe",
     * other views). Scope is restricted via `isContentProviderKey()` — header/
     * y-axis providers stay untouched, avoiding the race with PRD-007
     * `data-provider-ready`. Views accepting more than one content-provider key
     * (e.g. AbstractStandardTimelineView's calendar row, B-C4, alongside the main
     * B-D content area) get every one of them wiped, not just the primary key —
     * otherwise entities living only in the secondary provider (Urlaub/Feiertag
     * issues in the calendar project) never trigger that row's refresh.
     *
     * Both listeners use `bufferTime: 300` to coalesce bulk-save bursts (the
     * IssueHelper for-loop publishes one message per saved issue) into a single
     * refresh cycle.
     *
     * Cleanup is auto-wired via `disconnect: this.rxjs.disconnected$`.
     *
     * @private
     */
    private _registerIssueCreateDeleteListeners(): void {
        const broker = container.resolve(MessageBroker);
        const service = container.resolve(TimelineInvalidationService);
        const disconnect = this.rxjs.disconnected$;
        const isContentProvider = (key: string): boolean => this.isContentProviderKey(key);

        broker.registerListener(this, {
            messageType: 'entity.created',
            filterFn: (msg: BaseMessage<EntityCreatedPayload<Entity>>): boolean =>
                msg.payload.prototype.name === 'Issue',
            method: (_messages: Array<BaseMessage<EntityCreatedPayload<Entity>>>) => {
                service.invalidateAllProviders(isContentProvider);
                service.notifyInvalidated();
            },
            bufferTime: 300,
            disconnect,
        });

        broker.registerListener(this, {
            messageType: 'entity.deleted',
            filterFn: (msg: BaseMessage<EntityDeletedPayload<Entity>>): boolean =>
                msg.payload.entity.constructor.name === 'Issue',
            method: (messages: Array<BaseMessage<EntityDeletedPayload<Entity>>>) => {
                for (const m of messages) {
                    service.invalidateIssue(m.payload.entity.uid);
                }
                service.notifyInvalidated();
            },
            bufferTime: 300,
            disconnect,
        });
    }

    /**
     * Registers MessageBroker listeners for EmployeeToIssueAssignment create/delete/
     * change live-refresh — closes the same PRD-062 gap for assignee (Bearbeiter)
     * changes: adding/removing/editing an issue's assignment never publishes an
     * entity.* event for the Issue itself (IssueCommandBuilderService builds pure
     * assignment commands, no UpdateIssueCommand — see BulkCommandHandler), so
     * without this the Timeline never invalidates/re-renders on a Bearbeiter-only
     * change.
     *
     * Uses the SAME full-provider-wipe strategy as the Issue-create listener above
     * (not a per-item invalidateIssue()) deliberately: a per-item invalidation computes
     * the old position from the issue's `.employees`, and a refetch does NOT refresh an
     * issue that is already loaded — DtoToIssueTransformer returns loaded entities
     * unchanged. So `.employees` is only correct because the assignment command builder
     * derives it from the new assignments on save (IssueCommandBuilderService.buildCommands,
     * measured 2026-10-02: before that, a new or reassigned issue stayed in its old row or
     * in none on /by-assignee until the app was reloaded). The full wipe re-places every
     * issue in the visible regions from that list, old and new rows alike.
     *
     * @private
     */
    private _registerAssignmentChangeListeners(): void {
        const broker = container.resolve(MessageBroker);
        const service = container.resolve(TimelineInvalidationService);
        const disconnect = this.rxjs.disconnected$;
        const isContentProvider = (key: string): boolean => this.isContentProviderKey(key);
        const wipeAndNotify = (): void => {
            service.invalidateAllProviders(isContentProvider);
            service.notifyInvalidated();
        };

        broker.registerListener(this, {
            messageType: 'entity.created',
            filterFn: (msg: BaseMessage<EntityCreatedPayload<Entity>>): boolean =>
                msg.payload.prototype.name === 'EmployeeToIssueAssignment',
            method: wipeAndNotify,
            bufferTime: 300,
            disconnect,
        });

        broker.registerListener(this, {
            messageType: 'entity.deleted',
            filterFn: (msg: BaseMessage<EntityDeletedPayload<Entity>>): boolean =>
                msg.payload.entity.constructor.name === 'EmployeeToIssueAssignment',
            method: wipeAndNotify,
            bufferTime: 300,
            disconnect,
        });

        broker.registerListener(this, {
            messageType: 'entity.changed',
            filterFn: (msg: BaseMessage<EntityChangedPayload<Entity>>): boolean =>
                msg.payload.prototype.name === 'EmployeeToIssueAssignment',
            method: wipeAndNotify,
            bufferTime: 300,
            disconnect,
        });
    }

    /**
     * Handler for `entity.created` / `entity.deleted` on the Y-axis entity type
     * (O2D-PRD-096 Seam B — full re-init with a corrected origin).
     *
     * Fully re-initializes the Y-axis by reusing the EXISTING page-load init path
     * (`createStartCursors` + `createAxisConfigurations`), so the element's
     * `willUpdate` full-rebuild branch constructs a fresh `Axis` (with the corrected
     * origin re-read from `getViewCursorY`) plus a fresh `SpatialMatrix`. The user's
     * Y and X (time-axis) scroll positions are preserved instead of being reset.
     *
     * Re-init contract (each invariant guards a concrete failure mode):
     * - The OLD `data-provider-ready` gate + `updateStartCursor('D', '')` are GONE:
     *   the empty `''` cursor was the DIRECT cause of the `axis-expand` 400 (an empty
     *   `missingUid` reaching `expandToValue`). No race is reintroduced because the
     *   full-rebuild path builds the axis synchronously from the new `_startCursors`.
     * - `_refreshCursorXFromAppState()` runs FIRST so a re-init does not snap the time
     *   axis back to the stale connect-time date (the subclass hook re-reads
     *   `appState.dateCursor`; no-op on the base view).
     * - The `createStartCursors()` await is resolved into a LOCAL, then BOTH `@state`
     *   props are set SYNCHRONOUSLY in init-path order (`_axisConfigurations` first,
     *   then `_startCursors`) with NO `await` between, so a SINGLE `willUpdate` sees
     *   both fresh states and takes the `needsFullRebuild` branch.
     * - `setAxisConfigurationsAndWait(this._axisConfigurations)` is called SYNCHRONOUSLY
     *   right after the writes, passing the SAME reference just assigned, so the view
     *   render and this call coalesce into ONE rebuild the awaited resolver observes
     *   (a different reference or an intervening `await` → reference-dedup no-op → the
     *   resolver waits on a generation that never increments → guaranteed 5s timeout).
     * - The WHOLE body is `try/catch`-guarded: the MessageBroker invokes this `void`
     *   listener via RxJS `.subscribe(...)`, which fire-and-forgets the returned
     *   promise, so an unguarded rejection would surface as `Uncaught (in promise)` —
     *   the exact symptom this PRD eliminates. The catch only LOGS and RETURNS (no
     *   rollback, no re-dispatch): a `setAxisConfigurationsAndWait` timeout means the
     *   rebuild already committed and self-heals via the next-scroll cursor re-write,
     *   while a `createStartCursors()` rejection leaves the old axis fully intact.
     *
     * @param _messages - Buffered batch of matching messages (payload unused — any
     *                    create/delete for the Y-axis type triggers the same re-init)
     * @private
     */
    private async _onYAxisEntityCreatedOrDeleted(
        _messages: Array<BaseMessage<EntityCreatedPayload<Entity> | EntityDeletedPayload<Entity>>>,
    ): Promise<void> {
        await this.reinitializeAxes();
    }

    /**
     * Full Y-axis re-init through the page-load init path — the body of
     * `_onYAxisEntityCreatedOrDeleted`, whose doc block above states the contract.
     * Shared with views that must rebuild for another reason (BoardsView: the sort order
     * changed).
     *
     * `rebuildDataProviders: true` also recreates `_dataProviderConfigurations`. Needed when
     * the providers carry something that changed — BoardsView's content provider (B×D) carries
     * the sort order (timeline-data-providers.factory.ts). o2d-timeline treats changed
     * `dataProviderConfigurations` as a structural change and rebuilds them in the SAME
     * `willUpdate` as the axes. Without the option nothing changes for the other views.
     *
     * Every @state write stays synchronous after the one `await` (single-rebuild and
     * same-reference invariants, see above). Never rejects: errors are logged.
     *
     * @param options.rebuildDataProviders - also recreate the data provider configurations
     * @protected
     */
    protected async reinitializeAxes(options?: { rebuildDataProviders?: boolean }): Promise<void> {
        try {
            // Refresh the X (time) origin BEFORE re-init so the time axis is not
            // snapped back to the connect-time date (no-op on the base view).
            this._refreshCursorXFromAppState();

            // Resolve the corrected Y origin into a local BEFORE touching @state, so
            // the @state writes below stay synchronous (single-rebuild invariant).
            const startCursors = await this.createStartCursors();

            // Set the @state props synchronously in init-path order (axisConfigurations
            // first, then startCursors) so one `willUpdate` triggers `needsFullRebuild`.
            this._axisConfigurations = this.createAxisConfigurations();
            if (options?.rebuildDataProviders) {
                this._dataProviderConfigurations = this.createDataProviderConfigurations();
            }
            this._startCursors = startCursors;

            // Force the rebuild + await completion with the SAME reference, placed
            // synchronously right after the writes (same-reference invariant).
            await this._timeline?.setAxisConfigurationsAndWait(this._axisConfigurations);
        } catch (error) {
            // Log + return: the async re-init must never surface as an unhandled
            // rejection (RxJS fire-and-forgets this listener's promise). No rollback,
            // no re-dispatch — the axis self-heals on the next scroll / stays intact.
            console.error('Y-axis live re-init failed:', error);
        }
    }

    /**
     * Handler for `entity.changed` on the Y-axis entity type (PRD-043 T4.3).
     *
     * Rebuilds the axis configurations so Lit's `@state()` detects the reference
     * change and re-renders the Y-axis headers with the updated entity name
     * (e.g. after an IssueStatus rename).
     *
     * Does NOT reset the Y-cursor: a rename leaves the entity's position in the
     * sorted axis unchanged, so the user's scroll position must stay intact
     * (Goal 3 / Acceptance Scenario G3). Only create/delete resets the cursor.
     *
     * @param _messages - Buffered batch of matching messages (payload unused — any
     *                    `entity.changed` for the Y-axis type triggers the same rebuild)
     * @private
     */
    private _onYAxisEntityChanged(
        _messages: Array<BaseMessage<EntityChangedPayload<Entity>>>,
    ): void {
        // Rebuild axis configurations — creates a fresh BackendAxisDataService
        // instance so the renamed entity's new title is fetched from the backend.
        this._axisConfigurations = this.createAxisConfigurations();
    }

    // =======================================
    // Content Provider Key Hook
    // =======================================

    /**
     * Returns true if the given dataProviderKey belongs to a content provider
     * that this view handles. Override in subclasses to support multiple providers.
     *
     * Default implementation: exact match against contentProviderKey.
     * AbstractStandardTimelineView overrides this to also accept the calendar provider key.
     *
     * @param key - The dataProviderKey from a CellClickedEventDetail
     * @returns true if the key should trigger content cell handling
     * @protected
     */
    protected isContentProviderKey(key: string): boolean {
        return key === this.contentProviderKey;
    }

    // =======================================
    // Optional Hooks (MAY be overridden)
    // =======================================

    /**
     * Refreshes the X-axis cursor from `appState.dateCursor` before an axis
     * rebuild (O2D-PRD-096 Seam B).
     *
     * No-op on the base class — the base view has no time (X) axis cursor.
     * Subclasses that bake an X origin into their axis configurations (see
     * {@link AbstractStandardTimelineView}) override this to re-read the live
     * date cursor, so a re-init after a Y-axis delete does not snap the time
     * axis back to the stale connect-time date.
     *
     * @protected
     */
    protected _refreshCursorXFromAppState(): void {
        // No-op: base view has no X-axis cursor to refresh.
    }

    /**
     * Get Y-axis start cursor from ViewState or empty string for first visit.
     * Empty string is mapped to null in BackendAxisDataService (= start from first entity).
     *
     * Concrete views use this in createStartCursors() for the Y-axis cursor (typically axis 'D').
     *
     * @returns {string} ShortId from ViewState or empty string
     * @protected
     */
    protected getYAxisStartCursor(): string {
        return appState.getViewCursorY(this.viewStateName) ?? '';
        // '' wird in BackendAxisDataService zu null gemappt
    }

    /**
     * Axis ID for date cursor synchronization via AppState bridge.
     * Default: 'B' (backward compatibility with legacy views).
     * Return `null` to disable the AppState bridge entirely.
     *
     * The AppState bridge synchronizes appState.dateCursor (Unix timestamp) with the axis
     * specified by this property. When dateCursor changes, the corresponding axis start cursor
     * is updated reactively.
     *
     * @returns {string | null} Axis ID for date cursor sync, or null to disable
     * @protected
     */
    protected get dateCursorAxisId(): string | null {
        return 'B';
    }

    /**
     * Axis ID for Y-cursor synchronization (scroll → appState.setViewCursorY).
     * Default: 'D' (standard entity axis). Return null to disable Y-axis sync.
     *
     * @returns {string | null} Axis ID for Y-cursor sync, or null to disable
     * @protected
     */
    protected get yCursorAxisId(): string | null {
        return 'D';
    }

    /**
     * Toolbar items for this view.
     * Default: issue filters, reset to today, compact / standard / detail view and "add"
     * (defaultTimelineToolbarItems()).
     * Concrete views MAY override to add view-specific toolbar items.
     *
     * Example override:
     * ```typescript
     * protected getToolbarItems(): TimelineToolbarItem[] {
     *     return [
     *         ...super.getToolbarItems(),
     *         customViewSpecificItem,
     *     ];
     * }
     * ```
     *
     * @returns {TimelineToolbarItem[]} Array of toolbar items
     * @protected
     */
    protected getToolbarItems(): TimelineToolbarItem[] {
        return defaultTimelineToolbarItems();
    }

    /**
     * Hook called when the "Focus on Today" action fires (window event `timeline-focus-today`).
     *
     * Default implementation is a no-op. Override in views that need Y-axis adjustment
     * (e.g. BoardsView scrolls Y-axis to the project nearest to today).
     *
     * @protected
     */
    protected onFocusToday(): void {}

    // =======================================
    // Protected Helpers
    // =======================================

    /**
     * Updates the start cursor for a specific axis by creating a new array reference.
     *
     * Uses immutable `.map()` reassignment so Lit's `@state()` decorator detects the
     * reference change and triggers a re-render. Direct in-place mutation would be invisible
     * to Lit and must NOT be used.
     *
     * @param axisId - The axis identifier to update (e.g. 'D' for Y-axis)
     * @param value - New cursor value (ISO date string or numeric index)
     * @protected
     */
    protected updateStartCursor(axisId: string, value: string | number): void {
        this._startCursors = this._startCursors.map(c =>
            c.axisId === axisId ? { ...c, value } : c
        );
    }

    // =======================================
    // Lifecycle Methods
    // =======================================

    /**
     * Lifecycle: Element connected to DOM.
     * Initializes configurations synchronously, then asynchronously loads start cursors.
     *
     * Lifecycle order:
     * 1. Apply CSS variables via setStyleProperty() (synchronous)
     * 2. Load synchronous configurations (axes, areas, dataProviders, layout)
     * 3. Await createStartCursors() (asynchronous, may reject)
     * 4. Stale check (abandon if new connectedCallback started during await)
     * 5. Set _isLoading = false
     * 6. Initialize AppState bridge (AFTER start cursors exist, MAJOR-1)
     * 7. Register cursor-changed listener for scroll → AppState sync (PRD-036)
     * 8. Start midnight timer for automatic today-marker refresh (PRD-038)
     * 9. Listen for data-provider-ready events for timeline invalidation (PRD-048)
     * 10. Register Y-axis entity live-refresh listeners (PRD-043)
     * 11. Register Issue create/delete listeners (PRD-062)
     *
     * Error handling: If createStartCursors() rejects, logs error and sets _isLoading = false
     * to show empty timeline instead of infinite loading state (S-NEW-1).
     *
     * Stale generation guard (MINOR-R4-3): A generation counter prevents duplicate subscriptions
     * when fast route-switching triggers a new connectedCallback before await resolves.
     * The stale generation abandons post-await logic.
     *
     * @async
     */
    override async connectedCallback(): Promise<void> {
        super.connectedCallback();

        // Increment generation counter to detect stale async callbacks (MINOR-R4-3)
        const generation = ++this._connectedGeneration;

        // Initialize Ctrl+Key stream for issue-add mode (private method, DD-2)
        this.initCtrlKeyStream();

        // Initialize focus-today stream to react to window events (PRD-029)
        this.initFocusTodayStream();

        // Phase 1: Apply CSS variables (synchronous)
        // Light DOM CSS Inheritance Pattern:
        // Host element sets CSS Custom Properties via setStyleProperty() (inherited from AbstractO2dElement).
        // Descendants (<o2d-timeline>, <o2d-layout>, <o2d-area>) read these variables via var(--property-name)
        // because Light DOM allows natural CSS inheritance without Shadow DOM barriers (ADR-009).
        const sizeVars = this.getSectionSizeVariables();
        for (const [property, value] of Object.entries(sizeVars)) {
            this.setStyleProperty(property, value);
        }

        // Phase 1b: Reactive bridge for cell-width multiplication → CSS variable (PRD-060).
        // Must run in synchronous Phase 1 (before any await) to prevent initial-render flicker.
        this.initCellWidthMultiplicationBridge();

        // Phase 2: Load synchronous configurations
        this._axisConfigurations = this.createAxisConfigurations();
        this._areaConfigurations = this.createAreaConfigurations();
        this._dataProviderConfigurations = this.createDataProviderConfigurations();
        this._layoutTemplate = this.createLayoutTemplate();

        // Phase 3: Asynchronous start cursors with error handling (S-NEW-1)
        try {
            this._startCursors = await this.createStartCursors();
        } catch (error) {
            // A reload or navigation cancels this request mid-flight. That is not a failure the user
            // should see logged; a real network or server failure while the page stays still is.
            // Measured 2026-09-16: `pagehide` fires before the cancelled fetch rejects, so the flag
            // `pageLeaving` (module level, above) is already set when a page-leave rejection arrives here.
            if (!isPageLeaving()) {
                console.error('Failed to create start cursors:', error);
            }
            // _startCursors stays as empty array []. The bridge (Phase 5) will
            // harmlessly map over the empty array — no special handling needed.
            // _isLoading is set to false in Phase 4 below (always reached).
        }

        // Phase 4: Stale check - abandon if new connectedCallback started (MINOR-R4-3)
        if (generation !== this._connectedGeneration) {
            return; // Stale generation, abort to prevent duplicate subscriptions
        }

        // Phase 5: Loading complete
        this._isLoading = false;

        // Phase 6: AFTER createStartCursors(): Initialize AppState bridge (MAJOR-1)
        this.initAppStateBridge();

        // Phase 7: Register cursor-changed listener for scroll → AppState sync (PRD-036)
        this.initCursorChangedListener();

        // Phase 8: Start midnight timer for automatic today-marker refresh (PRD-038)
        this.initMidnightTimer();

        // Phase 9: Listen for data-provider-ready events for timeline invalidation (PRD-048)
        this.addEventListener('data-provider-ready', this._handleDataProviderReady);

        // Phase 10: Register Y-axis entity live-refresh listeners (PRD-043).
        // Opt-in per view via getYAxisEntityType() override — default returns null (no-op).
        // Disconnect is auto-wired to rxjs.disconnected$ (no manual cleanup needed).
        this._registerYAxisEntityListeners();

        // Phase 11: Register Issue create/delete listeners (PRD-062).
        // Wipes the content provider cache(s) + re-renders when an Issue is created or
        // deleted anywhere in the app. Scoped via isContentProviderKey().
        this._registerIssueCreateDeleteListeners();

        // Phase 12: Register EmployeeToIssueAssignment change listeners.
        // Same live-refresh gap as PRD-062, but for assignee (Bearbeiter) changes: a
        // pure assignment change (no other Issue field dirty) never publishes an
        // entity.* event for the Issue itself, so without this the Timeline never
        // invalidates/re-renders when a Bearbeiter is added/removed/edited.
        this._registerAssignmentChangeListeners();
    }

    /**
     * Lifecycle: Element disconnected from DOM.
     * Cleans up AppState subscription to prevent memory leaks (M-NEW-2).
     *
     * @override
     */
    override disconnectedCallback(): void {
        super.disconnectedCallback();

        // 1. Remove cursor-changed listener FIRST — prevents processing events while AppState sub is still active (PRD-036)
        this.removeEventListener('cursor-changed', this._handleCursorChanged);

        // 2. Clean up AppState subscription to prevent memory leak (M-NEW-2)
        this._appStateUnsubscriber?.();

        // 2b. Clean up cellWidthMultiplication subscription (PRD-060)
        this._cellWidthMultiplicationUnsubscriber?.();

        // 3. Clear axis entity metadata to prevent unbounded memory growth (C-1)
        axisEntityMetadata.clear();

        // 4. Remove data-provider-ready listener, unregister providers and callbacks (PRD-048/049)
        this.removeEventListener('data-provider-ready', this._handleDataProviderReady);
        if (this._registeredProviderKeys.size > 0) {
            const service = container.resolve(TimelineInvalidationService);
            for (const key of this._registeredProviderKeys) {
                service.removeOnInvalidatedCallback(key);
                service.unregisterProvider(key);
            }
            this._registeredProviderKeys.clear();
        }
    }

    /**
     * AppState unsubscriber function for cleanup in disconnectedCallback.
     * MUST be called to prevent memory leaks (M-NEW-2).
     * @private
     */
    private _appStateUnsubscriber?: () => void;

    /**
     * Dedicated unsubscriber for the cellWidthMultiplication → CSS-variable bridge (PRD-060).
     * Kept separate from _appStateUnsubscriber to avoid overwriting subscriptions.
     * MUST be called in disconnectedCallback to prevent memory leaks.
     * @private
     */
    private _cellWidthMultiplicationUnsubscriber?: () => void;

    /**
     * Guard flag: true while _handleCursorChanged is writing to appState.dateCursor.
     * Prevents initAppStateBridge subscriber from reacting to our own write (render-loop guard).
     * @private
     */
    private _isSyncingFromScroll = false;

    /**
     * Generation counter to prevent stale async connectedCallback from creating duplicate subscriptions.
     * Incremented on each connectedCallback. Checked after await to detect if a new connectedCallback started.
     * @private
     */
    private _connectedGeneration = 0;

    /**
     * Composite keys of the providers registered via data-provider-ready events.
     * Used for cleanup in disconnectedCallback. A view MAY accept more than one
     * content-provider key (see isContentProviderKey()) — e.g.
     * AbstractStandardTimelineView also registers its calendar row provider here.
     * @private
     */
    private readonly _registeredProviderKeys = new Set<string>();

    /**
     * Bound handler for data-provider-ready events from o2d-timeline.
     * Filters via isContentProviderKey() (NOT the raw contentProviderKey — a view
     * MAY accept multiple content-provider keys) and registers the provider in
     * TimelineInvalidationService.
     * @private
     */
    private readonly _handleDataProviderReady = (e: Event): void => {
        const detail = (e as CustomEvent<DataProviderReadyDetail>).detail;
        if (!detail || !this.isContentProviderKey(detail.key)) return;

        const service = container.resolve(TimelineInvalidationService);
        service.registerProvider(detail.key, detail.provider);
        service.setOnInvalidatedCallback(detail.key, () => this.triggerAreaReRender());
        this._registeredProviderKeys.add(detail.key);
    };

    /**
     * Initializes Ctrl+Key stream for issue-add mode (Ctrl+Click creates new issue).
     * Toggles 'ctrl-pressed' class on host for cursor: copy visual feedback.
     *
     * Note: Private method designed for future extraction into Reactive Controller (PRD-023).
     *
     * @private
     */
    private initCtrlKeyStream(): void {
        merge(
            fromEvent<KeyboardEvent>(window, 'keydown').pipe(
                filter(e => e.key === 'Control'),
                map(() => true),
            ),
            fromEvent<KeyboardEvent>(window, 'keyup').pipe(
                filter(e => e.key === 'Control'),
                map(() => false),
            ),
            fromEvent(window, 'blur').pipe(
                map(() => false),
            ),
        ).pipe(
            takeUntil(this.rxjs.disconnected$),
        ).subscribe(pressed => this.classList.toggle('ctrl-pressed', pressed));
    }

    /**
     * Initializes the window event listener for the `timeline-focus-today` event.
     *
     * Subscribes to the FOCUS_TODAY_EVENT dispatched by TIMELINE_JUMP_TO_TODAY action
     * and delegates to the protected `onFocusToday()` hook. Subscription is automatically
     * cleaned up when the element disconnects via `takeUntil(disconnected$)`.
     *
     * MUST be called after `super.connectedCallback()` so that the RxjsLifecycleController
     * is initialized and `disconnected$` is available.
     *
     * @private
     */
    private initFocusTodayStream(): void {
        fromEvent(window, FOCUS_TODAY_EVENT)
            .pipe(takeUntil(this.rxjs.disconnected$))
            .subscribe(() => this.onFocusToday());
    }

    /**
     * Triggers a re-render of all o2d-area elements by requesting a data refresh
     * from the o2d-timeline element. This increments stateManagerVersion via
     * DataProviderStateManager, ensuring Lit's render cycle propagates the new
     * version to all areas (Bug 1 fix: prevents version desync).
     * @private
     */
    private triggerAreaReRender(): void {
        this._timeline?.requestDataRefresh();
    }

    /**
     * Initializes the midnight timer. Calculates exact ms until next midnight,
     * triggers area re-render on midnight, and reschedules recursively for the next day.
     * Subscription is cleaned up automatically on disconnect via takeUntil(disconnected$).
     *
     * @param now - Optional DateTime for the current time (defaults to DateTime.now()). Used for testing.
     * @private
     */
    private initMidnightTimer(now?: DateTime): void {
        const currentTime = now ?? DateTime.now();
        const msUntilMidnight = calculateMsUntilMidnight(currentTime);

        timer(msUntilMidnight).pipe(
            takeUntil(this.rxjs.disconnected$),
        ).subscribe(() => {
            this.triggerAreaReRender();
            this.initMidnightTimer();
        });
    }

    /**
     * Registers the cursor-changed event listener on this element.
     * Must be called after initAppStateBridge() in connectedCallback().
     * @private
     */
    private initCursorChangedListener(): void {
        this.addEventListener('cursor-changed', this._handleCursorChanged);
    }

    /**
     * Handles cursor-changed events from the o2d-timeline element (user scroll + programmatic).
     * Syncs X-axis cursor value to appState.dateCursor (with render-loop guard) and
     * Y-axis cursor value to appState.setViewCursorY().
     *
     * Uses _isSyncingFromScroll flag (try/finally) to prevent the initAppStateBridge subscriber
     * from reacting to our own dateCursor write (would cause render-loop).
     *
     * @private
     */
    private readonly _handleCursorChanged = (e: Event): void => {
        const detail = (e as CustomEvent<CursorChangedDetail>).detail;
        if (!detail?.cursors) return;

        const xAxisId = this.dateCursorAxisId;
        const yAxisId = this.yCursorAxisId;

        for (const cursor of detail.cursors) {
            // X-axis: sync ISO date string to appState.dateCursor (Unix timestamp)
            if (xAxisId && cursor.axisId === xAxisId && typeof cursor.value === 'string') {
                const dt = DateTime.fromISO(cursor.value, { zone: 'utc' });
                if (dt.isValid && dt.toUnixInteger() !== appState.dateCursor) {
                    this._isSyncingFromScroll = true;
                    try {
                        appState.dateCursor = dt.toUnixInteger();
                    } finally {
                        this._isSyncingFromScroll = false;
                    }
                }
            }

            // Y-axis: sync entity UID to per-view cursor in appState
            if (yAxisId && cursor.axisId === yAxisId && typeof cursor.value === 'string' && this.viewStateName) {
                appState.setViewCursorY(this.viewStateName, cursor.value);
            }
        }
    };

    /**
     * Initializes AppState.dateCursor <-> startCursors bridge (reactive synchronization).
     * Subscribes to appState.dateCursor changes and updates the axis specified by dateCursorAxisId.
     *
     * The subscription is filtered at source to 'dateCursor' changes only (MAJOR-4).
     * This prevents unnecessary callback invocations on other AppState property changes.
     *
     * MUST be called AFTER createStartCursors() resolves (MAJOR-1).
     * Early-returns if dateCursorAxisId is null (MAJOR-2).
     * Stores unsubscriber for cleanup in disconnectedCallback (M-NEW-2).
     *
     * Note: Private method designed for future extraction into Reactive Controller (PRD-023).
     *
     * @private
     */
    private initAppStateBridge(): void {
        const axisId = this.dateCursorAxisId;

        // Bridge disabled if null (MAJOR-2)
        if (!axisId) {
            return;
        }

        // Subscribe to appState dateCursor changes (filtered at source, MAJOR-4).
        // Second param 'dateCursor' filters the subscription — callback only fires for dateCursor changes.
        // Returns unsubscribe function that MUST be called in disconnectedCallback (M-NEW-2).
        this._appStateUnsubscriber = appState.subscribe(() => {
            // Render-loop prevention (PRD-036): Skip if _handleCursorChanged triggered this
            // change. Works because @lit-app/state dispatches via EventTarget.dispatchEvent()
            // which is synchronous — the flag is still true when this callback fires.
            if (this._isSyncingFromScroll) return;

            const isoDate = DateTime.fromSeconds(appState.dateCursor, { zone: 'utc' }).toISODate();
            if (!isoDate) {
                console.warn('[AbstractO2dTimelineView] Invalid dateCursor in appState, cannot sync to axis');
                return;
            }

            // Type assertion: isoDate is string after null-check above
            const dateValue: string = isoDate;
            this._startCursors = this._startCursors.map(c =>
                c.axisId === axisId ? { ...c, value: dateValue } : c
            );
        }, 'dateCursor');
    }

    /**
     * Initializes the reactive bridge between appState.cellWidthMultiplication and
     * the CSS custom property `--cell-width-multiplication` on the host element (PRD-060).
     *
     * Writes the current multiplier synchronously to avoid initial-render flicker, then
     * subscribes to filtered appState changes ('cellWidthMultiplication' only, MAJOR-4 pattern).
     *
     * Descendants consume the variable via `calc(40px * var(--cell-width-multiplication, 1))`
     * in the designer CSS (Light DOM inheritance, ADR-009). The CSS-side fallback `1` ensures
     * correct rendering even when this bridge has not yet run (AC-7).
     *
     * Stores the unsubscriber in a dedicated slot (_cellWidthMultiplicationUnsubscriber) so it
     * does not overwrite the dateCursor subscription (Failure Condition #3).
     *
     * Note: Private method designed for future extraction into a Reactive Controller.
     *
     * @private
     */
    private initCellWidthMultiplicationBridge(): void {
        // Initial synchronous write — prevents first-paint flicker (Failure Condition #6).
        this.setStyleProperty('--cell-width-multiplication', String(appState.cellWidthMultiplication));

        // Filtered subscription: callback fires only for 'cellWidthMultiplication' changes.
        this._cellWidthMultiplicationUnsubscriber = appState.subscribe(() => {
            this.setStyleProperty('--cell-width-multiplication', String(appState.cellWidthMultiplication));
        }, 'cellWidthMultiplication');
    }

    // =======================================
    // Event Handlers (Private - Cell Interaction)
    // =======================================

    /**
     * Internal handler for cell-clicked events.
     * Filters by contentProviderKey and delegates to protected handleContentCellClicked hook.
     *
     * @param event - Custom event with CellClickedEventDetail
     * @private
     */
    private async _handleCellClicked(event: CustomEvent<CellClickedEventDetail>): Promise<void> {
        // Rapid-click guard (T4f.10-T4f.11)
        if (this._isProcessingClick) return;
        this._isProcessingClick = true;

        try {
            const detail = event.detail;

            // Filter: Only content area clicks (isContentProviderKey hook allows subclasses to accept multiple keys)
            if (!this.isContentProviderKey(detail.dataProviderKey)) {
                return;
            }

            // Delegate to protected hook (allows concrete views to override behavior)
            await this.handleContentCellClicked(detail);

        } catch (error) {
            console.error('[AbstractO2dTimelineView] Error handling cell click:', error);
        } finally {
            this._isProcessingClick = false;
        }
    }

    /**
     * Internal handler for cell-hover-changed events.
     * Filters by contentProviderKey and delegates to protected handleContentCellHover hook.
     *
     * @param event - Custom event with CellHoverEventDetail
     * @private
     */
    private _handleCellHover(event: CustomEvent<CellHoverEventDetail>): void {
        try {
            const detail = event.detail;

            // Filter: Only content areas (uses isContentProviderKey hook so subclasses can accept multiple keys)
            if (!this.isContentProviderKey(detail.dataProviderKey)) {
                return;
            }

            // Delegate to protected hook (allows concrete views to override behavior)
            this.handleContentCellHover(detail);

        } catch (error) {
            console.error('[AbstractO2dTimelineView] Error handling cell hover:', error);
        }
    }

    /**
     * Protected hook extracting the Issue entity from a content ViewportItem.
     * Default: The item's `.data` IS the Issue entity directly.
     *
     * Concrete views whose data provider wraps the Issue in an envelope type
     * (e.g. alongside scheduling metadata) MUST override this method — see
     * ResourcePlanningView.extractIssueFromItem() for the reference override
     * (mirrors the same wrapper it already unwraps in handleContentCellHover()).
     *
     * @param item - A content ViewportItem from CellClickedEventDetail
     * @protected
     */
    protected extractIssueFromItem(item: ViewportItem<unknown>): Issue {
        return item.data as Issue;
    }

    /**
     * Protected hook for content cell clicks.
     * Default: Opens IssueDialog with clicked issues. Supports Ctrl+Click for new issue creation.
     *
     * Concrete views MAY override to customize behavior.
     *
     * @param detail - Cell clicked event detail (already filtered by contentProviderKey)
     * @protected
     */
    protected async handleContentCellClicked(detail: CellClickedEventDetail): Promise<void> {
        // Close popover before opening dialog
        UiHelper.popover(null);

        // Filter: Only left-click
        if (detail.button !== 0) {
            return;
        }

        const issues: Issue[] = detail.items.map(item => this.extractIssueFromItem(item));
        let clickedIssue: Issue | null = detail.targetItem
            ? this.extractIssueFromItem(detail.targetItem)
            : null;

        // Guard: Empty cell without Ctrl - do nothing
        if (issues.length === 0 && !detail.modifiers.ctrlKey) {
            return;
        }

        // Create template issue with pre-filled date and employee
        const issueRepo = container.resolve(IssueRepository);
        const template = await issueRepo.create();

        // Parse date from X-axis (ISO date string) - ASSUMPTION: axisX.value is ISO date
        // Concrete views with different axis types MUST override this method
        const date = DateTime.fromISO(detail.axisX.value as string);
        if (!date.isValid) {
            console.warn('[AbstractO2dTimelineView] Invalid date on X-axis:', detail.axisX.value);
            return;
        }

        // Set template timeRange: date to date+1 day
        template.timeRange = Interval.fromDateTimes(date, date.plus({days: 1}));

        // Populate template from Y-axis entity (overridable hook)
        await this.populateTemplateFromYAxis(template, detail.axisY.value as string);

        // Ctrl+Click: Clone template, add to issue list, select as clicked issue
        if (detail.modifiers.ctrlKey) {
            const newIssue = await template.clone();
            issues.push(newIssue);
            clickedIssue = newIssue;
        }

        // Open IssuesDialog with issue list, pre-selected issue, and template
        IssueHelper.openIssuesEditDialog(issues, clickedIssue, template);
    }

    /**
     * Protected hook to populate a template issue from the Y-axis entity.
     * Called during cell-click handling to pre-fill the new issue template.
     *
     * Default: Looks up Employee by UID and assigns a new EmployeeToIssueAssignment.
     * Concrete views with different Y-axis entity types SHOULD override this method.
     *
     * template.employeeToIssueAssignments, NOT template.employees — the latter is a
     * plain settable Issue property too, but ha-employee-issue-assignment (the
     * "Mitarbeiter" field in ha-dialog-issue-edit) reads/binds
     * employeeToIssueAssignments (services/issue-form.service.ts: `key:
     * 'employeeToIssueAssignments'`); .employees is derived from the assignments when
     * the issue is loaded (DtoToIssueTransformer) or saved (IssueCommandBuilderService),
     * not by this template-construction path. Setting .employees here would silently
     * leave the dialog's Mitarbeiter field empty. Uses EmployeeToIssueAssignmentRepository.create()
     * — the same client-side construction the field's own "+" button uses
     * (ha-employee-issue-assignment.component.ts:_onClickAdd).
     *
     * @param template - The template issue to populate
     * @param yAxisUid - The ShortId from the Y-axis (entity UID)
     * @protected
     */
    protected async populateTemplateFromYAxis(template: Issue, yAxisUid: string): Promise<void> {
        const employeeRepo = container.resolve(EmployeeRepository);
        const employee = await employeeRepo.findOneById(yAxisUid);
        if (employee) {
            const assignment = await container.resolve(EmployeeToIssueAssignmentRepository).create(template, employee);
            if (assignment) {
                template.employeeToIssueAssignments = [assignment];
            }
        }
    }

    /**
     * Protected hook for content cell hover.
     * Default: Shows issue popover on 'enter', hides on 'leave'.
     *
     * Concrete views MAY override to customize popover content or disable popover entirely.
     *
     * @param detail - Cell hover event detail (already filtered by contentProviderKey)
     * @protected
     */
    protected handleContentCellHover(detail: CellHoverEventDetail): void {
        if (detail.state === 'enter') {
            const targetOrFirst = detail.targetItem ?? detail.items?.[0];
            if (targetOrFirst) {
                const issue = targetOrFirst.data as Issue;
                UiHelper.popover(html`
                    <ha-issue-preview .issue=${issue}></ha-issue-preview>
                `);
            }
        } else {
            UiHelper.popover(null);
        }
    }

    /**
     * Renders the timeline view with toolbar and loading state.
     *
     * Loading state: Shows i18n placeholder via translate('timeline.loading') (N-NEW-3, MINOR-1)
     * Loaded state: Shows toolbar (above) + o2d-timeline element (below)
     *
     * Structure (TR-6):
     * - Toolbar (ToolbarHelper.renderTimelineToolbar with items from getToolbarItems)
     * - o2d-timeline element (binds to all @state configurations and start cursors)
     *
     * @returns {TemplateResult | typeof nothing} The rendered template
     * @protected
     */
    protected override render(): TemplateResult | typeof nothing {
        if (this._isLoading) {
            return html`<div class="loading">${translate('timeline.loading')}</div>`;
        }

        // Toolbar rendered above o2d-timeline (TR-6)
        const toolbar = ToolbarHelper.renderTimelineToolbar(this.getToolbarItems());

        // o2d-timeline element with all configurations bound to @state properties (TR-6, T4e)
        // Event handlers: @cell-clicked and @cell-hover-changed (Phase 4f)
        return html`
            <div class="view__body">
                <div class="view__column">
                    ${toolbar}
                    <o2d-timeline
                        @cell-clicked=${this._handleCellClicked}
                        @cell-hover-changed=${this._handleCellHover}
                        .layoutTemplate=${this._layoutTemplate}
                        .startCursors=${this._startCursors}
                        .axisConfigurations=${this._axisConfigurations}
                        .areaConfigurations=${this._areaConfigurations}
                        .dataProviderConfigurations=${this._dataProviderConfigurations}
                    ></o2d-timeline>
                </div>
                <!--
                    Drawer open/collapse reuses the shared \`todoFilterOpen\` slot for
                    every mode (incl. Resource). The Resource view has no ToDo drawer to
                    conflict with, so no dedicated \`sidebar-toggle-resource\` slot is
                    needed (PRD-100 Chosen defaults). The embedded filter form targets
                    \`this.filterSlot\` so a subclass isolates its data slot without a
                    separate open/close slot.
                -->
                <div class="sidebar__filter ${appState.todoFilterOpen ? 'expanded' : 'collapsed'}">
                    <main class="sidebar__filter__main">
                        <ha-form-issue-filters class="wrapper" .filterSlot=${this.filterSlot}></ha-form-issue-filters>
                    </main>
                </div>
            </div>
        `;
    }
}
